import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateAgent, deleteAgent, searchAgent } from "../../../RTK/AgentsSlice";
import ConfirmationModal from "./ConfirmationModal";
import SearchResultsPopup from "./SearchResultsPopup ";
import { 
  X,
  Search,
  Edit2,
  Trash2,
  Save,
  User,
  DollarSign,
  CheckCircle,
  XCircle,
  UserPlus
} from "lucide-react";

const AgentPopup = ({ agent, setShowAgentPopup }) => {
  const [parentId, setParentId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (agent) setParentId(agent.parent_id || "");
  }, [agent]);

  const handleParentIdChange = (e) => setParentId(e.target.value);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleSearchClick = async () => {
    const response = await dispatch(searchAgent({ agent_id: agent.agent_id, query: searchQuery }));
    setResults(response.payload || []);
    setIsResultsOpen(true);
  };

  const handleDeleteClick = () => setIsModalOpen(true);

  const handleConfirmDelete = () => {
    dispatch(deleteAgent(agent.id)).then(() => setShowAgentPopup(false));
    setIsModalOpen(false);
  };

  const handleSaveClick = async () => {
    if (agent) {
      const updatedData = {
        id: agent.id,
        product: agent.product,
        agent: agent.id,
        parent_id: parentId,
        agent_price: agent.agent_price,
      };
      await dispatch(updateAgent(updatedData));
      setShowAgentPopup(false);
    }
  };

  if (!agent) return null;

  return (
    <>
      {/* Background Overlay */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[4000]" />

      {/* Popup Container */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-[4999]" dir="rtl">
        <div className="bg-gradient-to-b bg-gray-900  rounded-2xl border border-gray-800/50 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="relative flex justify-between items-center p-6 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-gray-700/50 after:to-transparent">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <User className="w-6 h-6 text-[#FEC111]" />
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                تفاصيل الوكيل
              </span>
            </h2>
            <button
              onClick={() => setShowAgentPopup(false)}
              className="text-gray-400 w-10 h-10 rounded-full flex items-center justify-center bg-gray-800/50 hover:bg-[#FEC111] hover:text-black transition-all duration-300 hover:scale-105"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Search Bar */}
            <div className="flex gap-3">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="البحث عن تفاصيل الوكيل..."
                className="flex-1 px-4 py-3 rounded-xl bg-gray-900/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors placeholder:text-gray-500"
              />
              <button
                onClick={handleSearchClick}
                className="px-6 rounded-xl bg-[#FEC111] text-black font-bold hover:bg-[#FEC111]/90 transition-colors duration-300 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                بحث
              </button>
            </div>

            {/* Agent Details */}
            <div className="space-y-5">
              <DetailItem 
                icon={User}
                label="اسم الوكيل" 
                value={agent.agent || "غير متوفر"}
              />
              <DetailItem 
                icon={DollarSign}
                label="سعر الوكيل" 
                value={agent.agent_price || "غير متوفر"}
              />
              <DetailItem 
                icon={agent.is_product_available ? CheckCircle : XCircle}
                label="الحالة" 
                value={agent.is_product_available ? "متوفر" : "غير متوفر"}
                valueColor={agent.is_product_available ? "text-emerald-400" : "text-rose-400"}
              />

              {/* Editable Parent ID */}
              <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-800/50">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-gray-400">
                    <UserPlus className="w-5 h-5 text-[#FEC111]" />
                    <span>معرف الوكيل</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={parentId}
                      onChange={handleParentIdChange}
                      placeholder="تعديل"
                      className="w-32 px-3 py-2 rounded-lg bg-slate-800/60 text-white border border-gray-700/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors"
                    />
                    
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-4 pt-4">
              <button
                onClick={handleDeleteClick}
                className="flex items-center justify-center gap-2 py-3 px-6 bg-gray-900/50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all duration-300 font-bold"
              >
                <Trash2 className="w-5 h-5" />
                حذف الوكيل
              </button>
              <button
                onClick={handleSaveClick}
                className="flex items-center justify-center gap-2 py-3 px-6 bg-[#FEC111] text-black rounded-xl hover:bg-[#FEC111]/90 transition-all duration-300 font-bold"
              >
                <Save className="w-5 h-5" />
                حفظ التغييرات
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {/* Search Results Popup */}
      {isResultsOpen && (
        <SearchResultsPopup
          results={results}
          onClose={() => setIsResultsOpen(false)}
        />
      )}
    </>
  );
};

// Detail Item Component
const DetailItem = ({ icon: Icon, label, value, valueColor = "text-white" }) => (
  <div className="bg-slate-800/50 p-4 rounded-xl border border-gray-800/50">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 text-gray-400">
        <Icon className="w-5 h-5 text-[#FEC111]" />
        <span>{label}</span>
      </div>
      <span className={`font-bold ${valueColor}`}>{value}</span>
    </div>
  </div>
);

export default AgentPopup;